import React from 'react';
import { motion } from 'framer-motion';
import TiltedCard from './reactbits/TiltedCard';
import CountUp from './reactbits/CountUp';

const Hero = ({ tagline = "Education" }) => {
  return (
    <section id="home" className="relative min-h-screen pt-28 pb-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-10 items-start">

          {/* Left Column */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 md:p-10"
            >
              <div className="flex flex-col sm:flex-row items-start gap-5 mb-7">
                <div className="shrink-0 rounded-xl border border-slate-200 bg-white p-3">
                  <img
                    src="/logo.png"
                    alt="Vaishnavi Educare Logo"
                    className="h-14 w-auto"
                  />
                </div>
                <div>
                  <p className="mb-3 inline-flex rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-medium text-primary-800">
                    Admissions counselling for serious decisions
                  </p>
                  <h1 className="text-display font-semibold font-heading tracking-[-0.025em] text-gray-950" style={{textWrap: 'balance'}}>
                    Vaishnavi Educare
                  </h1>
                  <h2 className="mt-3 text-xl md:text-2xl text-gray-600 font-sans">
                    Your trusted {tagline?.toLowerCase()} advisor
                  </h2>
                </div>
              </div>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-2xl text-lg text-gray-700 mb-8 leading-relaxed"
                style={{textWrap: 'pretty'}}
              >
                Experienced counselling for admissions in{' '}
                <strong className="font-semibold text-primary-800">Medical</strong>,{' '}
                <strong className="font-semibold text-primary-800">BAMS</strong>,{' '}
                <strong className="font-semibold text-primary-800">Engineering</strong>, and{' '}
                <strong className="font-semibold text-primary-800">Study Abroad</strong>{' '}
                programs. We help families compare options, understand counselling steps, and make admission decisions with clarity.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-wrap gap-3"
              >
                <a
                  href="#contact"
                  className="px-7 py-3 bg-primary-800 text-white rounded-full font-medium hover:bg-primary-900 transition-colors duration-200"
                >
                  Request Counselling
                </a>
                <a
                  href="#neet-predictor"
                  className="px-7 py-3 bg-white text-primary-800 border border-primary-200 rounded-full font-medium hover:border-primary-400 hover:bg-primary-50 transition-colors duration-200"
                >
                  Check NEET Rank
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.32 }}
                className="mt-10 grid grid-cols-3 gap-4 border-t border-slate-200 pt-6"
              >
                {[
                  { value: 15, suffix: '+', label: 'Years of Experience' },
                  { value: 5000, suffix: '+', label: 'Students Guided' },
                  { value: 100, suffix: '+', label: 'Partner Colleges' }
                ].map((stat, index) => (
                  <div key={index} className="text-left">
                    <div className="text-2xl md:text-3xl font-semibold text-primary-800 font-heading tabular-nums">
                      <CountUp
                        from={0}
                        to={stat.value}
                        duration={1.4}
                        suffix={stat.suffix}
                        separator=","
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-5 hidden md:block space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading">Featured Programs</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { title: 'MBBS in India', imageSrc: '/photos/mbbs-students.jpg', details: 'Secure seats in top medical colleges', link: '#mbbs-india' },
                  { title: 'Engineering', imageSrc: '/Engineering.jpg', details: 'Admission in top engineering colleges', link: '#engineering' },
                  { title: 'MBBS in Nepal', imageSrc: '/photos/kathmandu.jpg', details: 'Kathmandu University affiliated colleges', link: '#study-abroad' },
                  { title: 'BAMS', imageSrc: '/photos/mbbs-students-2.jpg', details: 'Ayurvedic colleges in UP & Uttarakhand', link: '#bams-colleges' }
                ].map((program, index) => (
                  <a key={index} href={program.link} className="block">
                    <TiltedCard
                      imageSrc={program.imageSrc}
                      altText={program.title}
                      containerHeight="160px"
                      containerWidth="100%"
                      rotateAmplitude={1.5}
                      scaleOnHover={1.015}
                      showTooltip={false}
                      showMobileWarning={false}
                      overlayContent={
                        <div className="p-3 text-white">
                          <h4 className="text-sm font-semibold font-heading">{program.title}</h4>
                          <p className="text-xs opacity-90">{program.details}</p>
                        </div>
                      }
                    />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* NEET Predictor CTA card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="bg-gradient-to-br from-primary-800 to-primary-700 rounded-2xl shadow-sm p-5 text-white"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
                  Free Tool
                </span>
              </div>
              <h3 className="text-lg font-semibold font-heading mb-1">NEET College Predictor</h3>
              <p className="text-sm text-primary-100 mb-4 leading-relaxed">
                Enter your rank → instantly see which medical colleges you qualify for, with fee details and predicted counselling round.
              </p>
              <ul className="space-y-1.5 mb-5">
                {[
                  'All India + State Quota seats',
                  'Filter by fee range & state',
                  'Live admission cutoff data',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-primary-100">
                    <span className="h-1 w-1 rounded-full bg-primary-200 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#neet-predictor"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-primary-800 rounded-full text-sm font-semibold hover:bg-primary-50 transition-colors duration-200"
              >
                Get Prediction Now →
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 bg-white rounded-2xl border border-primary-100 shadow-sm p-6"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 font-heading">Speak with an admissions advisor — free, no commitment</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                <a href="tel:+918080779602" className="text-sm font-semibold text-primary-800 hover:underline">+91 80807 79602</a>
                <span className="text-slate-300 hidden sm:block">|</span>
                <a href="tel:+918275599604" className="text-sm font-semibold text-primary-800 hover:underline">+91 82755 99604</a>
                <span className="text-xs text-gray-400">No advance payment required</span>
              </div>
            </div>
            <div className="flex gap-3 shrink-0">
              <a
                href="https://wa.me/918080779602"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors duration-200 text-sm whitespace-nowrap"
              >
                WhatsApp
              </a>
              <a
                href="#contact"
                className="px-5 py-2.5 bg-primary-800 text-white rounded-full font-medium hover:bg-primary-900 transition-colors duration-200 text-sm whitespace-nowrap"
              >
                Contact Us
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
